// Classic's home sections (ARCHITECTURE §4.4): today's Home.tsx
// renderSection() split per section type, markup and classes verbatim. Each
// view reads its slot and its slice of the home VM. The scroll state each
// section used to borrow from Home (the upcoming fade, the horizontal
// scrollers) now lives in the section, and the gallery opens Home's lightbox
// through ClassicHomeContext.

import { useContext, useEffect, useRef, useState, type CSSProperties } from 'react'
import { ClassicHomeContext } from './context'
import { Link } from 'react-router-dom'
import SwipeableStandings from '../components/SwipeableStandings'
import HomeDivisionStandings from '../components/HomeDivisionStandings'
import { GameCardSkeleton, ResultCardSkeleton } from '../components/Skeleton'
import VenueLink from '@/components/VenueLink'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import { useTeamDisplay } from '@/utils/teamDisplay'
import { sf } from '../../../core/features/sf'
import { useLabels } from '../../../core/labels'
import type { HomeSectionView } from '../../../core/pages/home/types'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'


// #710: with 2+ live divisions, one compact table per division (Classic Home is always All divisions).
export const Standings: HomeSectionView<'standings'> = ({ data, vm }) => (
  vm.division.enabled
    ? <HomeDivisionStandings featured={data.featured} loading={data.loading} linkSchedule={vm.gating.sections.schedule} />
    : <SwipeableStandings featured={data.featured} loading={data.loading} />
)

export const Upcoming: HomeSectionView<'upcoming'> = ({ slot, data }) => {
  const t = useLabels()
  const c = useT(siteClassicMessages).home
  const teamName = useTeamDisplay()
  const upcomingScrollRef = useRef<HTMLDivElement>(null)
  const [showUpcomingFade, setShowUpcomingFade] = useState(false)
  const upcomingGames = data.games

  // Check if upcoming matches can scroll (vertical)
  useEffect(() => {
    const checkScroll = () => {
      if (upcomingScrollRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = upcomingScrollRef.current
        setShowUpcomingFade(scrollHeight > clientHeight && scrollTop < scrollHeight - clientHeight - 10)
      }
    }
    checkScroll()
    const el = upcomingScrollRef.current
    if (el) {
      el.addEventListener('scroll', checkScroll)
      window.addEventListener('resize', checkScroll)
      return () => {
        el.removeEventListener('scroll', checkScroll)
        window.removeEventListener('resize', checkScroll)
      }
    }
  }, [upcomingGames])

  const loading = data.loading
  return (
          <div className="bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 px-4 py-4" {...(!loading && upcomingGames.length > 0 ? sf('home.section.upcoming') : {})}>
                    <div className="flex items-baseline justify-between mb-3">
                      <h2 id="games" className="text-sm sm:text-base font-['Montserrat'] font-black text-white uppercase tracking-tight">
                        {slot.title}
                      </h2>
                      <span className="text-primary text-[10px] uppercase tracking-[0.22em] font-bold">
                {loading ? '...' : c.nextCount(Math.min(data.total, 6))}
                      </span>
                    </div>

                    <div className="relative">
                      <div
                        ref={upcomingScrollRef}
                        className="max-h-[420px] overflow-y-auto scrollbar-hide space-y-3 pr-2"
                      >
                {loading ? (
                  <>
                    <GameCardSkeleton />
                    <GameCardSkeleton />
                    <GameCardSkeleton />
                  </>
                ) : upcomingGames.length > 0 ? (
                  // Sorted live first and capped at 6 by core.
                  upcomingGames
                    .map((game) => {
                      const { date, time, tzAbbrev, localTime, localTzAbbrev } = game.when
                      const isLive = game.live
                      return (
                        <Link
                          key={game.id}
                          to={game.href}
                          className={`block px-4 py-3 transition-all relative overflow-hidden ${
                            isLive
                              ? 'bg-gradient-to-r from-red-500/20 via-red-500/15 to-black/20 border-2 border-red-500/50 hover:border-red-500/70 shadow-lg shadow-red-500/20'
                              : 'bg-bg-primary/15 border border-secondary/10 hover:border-primary/25'
                          }`}
                        >
                          {/* Pulsing background effect for live games */}
                          {isLive && (
                            <div className="absolute inset-0 bg-red-500/10 animate-pulse"></div>
                          )}

                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${
                                isLive ? 'text-red-400' : 'text-primary'
                              }`}>
                                {date}
                              </span>
                              <div className="flex items-center gap-2">
                                {isLive && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/30 border border-red-500/50 text-red-300 text-[9px] uppercase tracking-wider font-black rounded animate-pulse">
                                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                                    {c.liveBadge}
                                  </span>
                                )}
                                <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${
                                  isLive ? 'text-red-300' : 'text-white/75'
                                }`}>
                                  {time}{tzAbbrev ? ` ${tzAbbrev}` : ''}
                                </span>
                                {localTime && (
                                  <span className="text-[9px] text-white/60">
                                    ({localTime} {localTzAbbrev})
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className={`font-['Montserrat'] font-black text-base leading-none ${
                                isLive ? 'text-white' : 'text-white'
                              }`}>
                                {teamName({ name: game.home.name, short_code: game.home.shortCode }, 'auto')}
                              </span>
                              <span className={`text-[10px] uppercase tracking-wider ${
                                isLive ? 'text-white/60' : 'text-white/60'
                              }`}>{t.term('homeSide')}</span>
                            </div>
                            <div className={`h-px my-2 ${
                              isLive ? 'bg-red-500/30' : 'bg-white/10'
                            }`}></div>
                            <div className="flex items-center justify-between">
                              <span className={`font-['Montserrat'] font-black text-base leading-none ${
                                isLive ? 'text-white' : 'text-white'
                              }`}>
                                {teamName({ name: game.away.name, short_code: game.away.shortCode }, 'auto')}
                              </span>
                              <span className={`text-[10px] uppercase tracking-wider ${
                                isLive ? 'text-white/60' : 'text-white/60'
                              }`}>{t.term('awaySide')}</span>
                            </div>
                            <div className="mt-2 flex items-center justify-between">
                              <span className={`text-[10px] uppercase tracking-wider ${
                                isLive ? 'text-white/50' : 'text-white/60'
                              }`}>
                                {game.venue
                                  ? <VenueLink
                                      venueId={game.venue.id}
                                      venueName={game.venue.name}
                                      parentVenueId={game.venue.parentId}
                                      parentVenueName={game.venue.parentName}
                                    />
                                  : t.ui.common.tbd}
                              </span>
                              {game.hasStream && (
                                <span className={`text-[10px] uppercase tracking-wider font-bold ${
                                  isLive ? 'text-red-300' : 'text-primary'
                                }`}>
                                  📺 {t.ui.home.streamBadge(isLive)}
                                </span>
                              )}
                            </div>
                          </div>
                        </Link>
                      )
                    })
                ) : (
                  <div className="text-white/60 text-center py-8">
                    {t.ui.home.upcomingEmpty}
                          </div>
                )}
                      </div>
                      {showUpcomingFade && (
                        <div className="absolute bottom-0 left-0 right-2 h-12 bg-gradient-to-t from-bg-secondary/45 to-transparent pointer-events-none"></div>
                      )}
                    </div>
          </div>
  )
}

// Fade the scroller's clipped edge(s) under the scroll buttons, so a
// half-shown result card reads as "more this way" instead of the button
// sitting on its text (#791): fully transparent under the 32px button,
// fading in over the next 56px.
function scrollEdgeMask(left: boolean, right: boolean): CSSProperties {
  if (!left && !right) return {}
  const mask = `linear-gradient(to right, ${left ? 'transparent, transparent 32px, #000 88px' : '#000'}, ${right ? '#000 calc(100% - 88px), transparent calc(100% - 32px)' : '#000'})`
  return { maskImage: mask, WebkitMaskImage: mask }
}

export const RecentResults: HomeSectionView<'recent_results'> = ({ slot, data }) => {
  const c = useT(siteClassicMessages).home
  const teamName = useTeamDisplay()
  const recentResults = data.results
  const loading = data.loading
  // Horizontal scroll for results section
  const {
    scrollRef: resultsScrollRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft: scrollResultsLeft,
    scrollRight: scrollResultsRight,
  } = useHorizontalScroll({ deps: [recentResults] })

  return (
          <div className="bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 px-4 py-4" {...(!loading && recentResults.length > 0 ? sf('home.section.recent_results') : {})}>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm sm:text-base font-['Montserrat'] font-black text-white uppercase tracking-tight">
                {slot.title}
              </h2>
                        <span className="text-white/45 text-[10px] uppercase tracking-[0.22em] font-bold">
                          {c.latest}
                        </span>
                      </div>
                      <div className="relative">
                        {canScrollLeft && (
                          <button
                            onClick={scrollResultsLeft}
                            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-bg-primary/60 hover:bg-bg-primary/80 border border-secondary/20 hover:border-primary/40 rounded-full flex items-center justify-center transition-all"
                            aria-label={c.scrollLeft}
                          >
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                        )}

                        <div
                          ref={resultsScrollRef}
                          className="overflow-x-auto scrollbar-hide scroll-smooth"
                          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', ...scrollEdgeMask(canScrollLeft, canScrollRight) }}
                        >
                          <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
                  {loading ? (
                    <>
                      <ResultCardSkeleton />
                      <ResultCardSkeleton />
                      <ResultCardSkeleton />
                    </>
                  ) : recentResults.length > 0 ? (
                    recentResults.map((result) => (
                      <Link
                        key={result.id}
                        to={result.href}
                        className="flex-shrink-0 w-48 bg-bg-primary/15 border border-secondary/10 px-3 py-3 hover:border-primary/25 transition-all"
                      >
                                <div className="flex items-center justify-between">
                          <span className="text-white/85 font-bold text-xs">
                            {teamName({ name: result.home.name, short_code: result.home.shortCode }, 'auto')}
                          </span>
                          <span className={`font-black text-base ${result.home.score > result.away.score ? 'text-primary' : 'text-white/60'}`}>
                            {result.home.score}
                          </span>
                                </div>
                                <div className="flex items-center justify-between">
                          <span className="text-white/85 font-bold text-xs">
                            {teamName({ name: result.away.name, short_code: result.away.shortCode }, 'auto')}
                          </span>
                          <span className={`font-black text-base ${result.away.score > result.home.score ? 'text-primary' : 'text-white/60'}`}>
                            {result.away.score}
                          </span>
                                </div>
                      </Link>
                    ))
                  ) : (
                    <div className="text-white/60 text-center py-4 w-48">
                      {c.noResults}
                              </div>
                  )}
                          </div>
                        </div>

              {canScrollRight && recentResults.length > 2 && (
                          <button
                            onClick={scrollResultsRight}
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-bg-primary/60 hover:bg-bg-primary/80 border border-secondary/20 hover:border-primary/40 rounded-full flex items-center justify-center transition-all"
                            aria-label={c.scrollRight}
                          >
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
  )
}

export const Sponsors: HomeSectionView<'sponsors'> = ({ slot, data }) => {
  const c = useT(siteClassicMessages).home
  const sponsors = data.tiers
  const getLogoSize = (levelIndex: number, totalLevels: number) => {
    if (totalLevels === 1) return 'w-24 h-24'
    if (levelIndex === 0) return 'w-24 h-24'
    if (levelIndex === 1) return 'w-20 h-20'
    if (levelIndex === 2) return 'w-16 h-16'
    return 'w-14 h-14'
  }

  const getLevelStyle = (levelIndex: number, totalLevels: number) => {
    if (totalLevels === 1) return { border: 'border-primary/30', bg: 'bg-white/[0.04]', text: 'text-primary' }
    if (levelIndex === 0) return { border: 'border-primary/30', bg: 'bg-white/[0.04]', text: 'text-primary' }
    if (levelIndex === 1) return { border: 'border-secondary/20', bg: 'bg-white/[0.03]', text: 'text-primary/90' }
    return { border: 'border-secondary/15', bg: 'bg-white/[0.02]', text: 'text-white/60' }
  }

  return (
          <div className="bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-4 py-4" {...(sponsors.length > 0 ? sf('home.section.sponsors') : {})}>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-['Montserrat'] font-black text-white uppercase tracking-tight">
                {slot.title}
              </h3>
            </div>

            {sponsors.length === 0 ? (
              <div className="text-center text-white/60 py-4 text-sm">{c.noSponsors}</div>
            ) : (
              <div className="space-y-4">
                {sponsors.map((level, levelIndex) => {
                  const style = getLevelStyle(levelIndex, sponsors.length)
                  const logoSize = getLogoSize(levelIndex, sponsors.length)

                  return (
                    <div key={level.id}>
                      <div className={`${style.text} text-[10px] uppercase tracking-[0.22em] font-bold mb-3`}>
                        {level.name}
                      </div>
                      <div className="flex flex-wrap justify-center gap-3">
                        {level.sponsors.map((sponsor) => {
                          // Calculate width based on tier to match grid columns
                          const getSponsorWidth = () => {
                            if (levelIndex === 0) return 'w-[calc(50%-0.375rem)]' // 2 per row
                            if (levelIndex === 1) return 'w-[calc(33.333%-0.5rem)]' // 3 per row
                            return 'w-[calc(25%-0.5625rem)]' // 4 per row
                          }
                          return (
                            <Link
                              key={sponsor.id}
                              to={sponsor.href}
                              className={`${style.bg} ${style.border} border px-4 py-4 flex flex-col items-center justify-center hover:border-primary/40 transition-all cursor-pointer ${getSponsorWidth()} max-w-[300px]`}
                            >
                            <div className={`${logoSize} mb-3 bg-fixed-white rounded-lg flex items-center justify-center overflow-hidden`}>
                              {sponsor.logoUrl ? (
                                <img
                                  src={sponsor.logoUrl || ''}
                                  alt={sponsor.name}
                                  className="max-w-full max-h-full object-contain p-2"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none'
                                    const next = e.currentTarget.nextElementSibling as HTMLElement | null
                                    if (next) next.style.display = 'flex'
                                  }}
                                />
                              ) : null}
                              <div className={`${sponsor.logoUrl ? 'hidden' : 'flex'} items-center justify-center text-gray-400 text-xs`}>
                                {c.logoPlaceholder}
                              </div>
                            </div>
                            <span className="text-white/90 text-xs uppercase tracking-wider text-center font-bold">
                              {sponsor.name}
                            </span>
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
  )
}

export const Announcements: HomeSectionView<'announcements'> = ({ data }) => {
  const c = useT(siteClassicMessages).home
  const core = useT(siteCoreMessages)
  const announcements = data.items
  if (announcements.length === 0) return null
  return (
          <div className="bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-6 py-6" {...sf('home.section.announcements')}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-['Montserrat'] font-black text-white uppercase tracking-tight">{core.pages.home.sections.announcements}</h4>
              {data.hasLive && (
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse"></span>
                  <span className="text-red-500 text-xs uppercase tracking-wider font-bold">{core.labels.gameStatus.live}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {announcements.slice(0, 6).map((a) => {
                // Ticket-linked announcements get a "Get Tickets" CTA pill so
                // fans can deep-link to /tickets/<slug>.
                const hasTicketCta = !!a.ticketSaleHref
                const card = (
                  <div className={`bg-bg-primary/20 border border-secondary/10 px-4 py-3 ${a.link ? 'hover:border-primary/30 transition-colors cursor-pointer' : ''}`}>
                    <div className={`text-[10px] uppercase tracking-[0.22em] font-bold mb-2 ${a.isLiveEvent ? 'text-red-500' : 'text-primary'}`}>{a.label}</div>
                    <div className="text-white/85 text-sm leading-snug">{a.text}</div>
                    {hasTicketCta && (
                      <div className="mt-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-black font-bold uppercase tracking-wider text-[10px]">
                          {c.getTickets}
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    )}
                  </div>
                )
                // #260: core sanitised the link (http(s)/mailto/tel or a same-site path).
                if (a.link) {
                  return a.link.external
                    ? <a key={a.key} href={a.link.href} target="_blank" rel="noopener noreferrer">{card}</a>
                    : <Link key={a.key} to={a.link.href}>{card}</Link>
                }
                return <div key={a.key}>{card}</div>
              })}
            </div>
          </div>
  )
}

export const Gallery: HomeSectionView<'gallery'> = ({ slot, data }) => {
  const c = useT(siteClassicMessages).home
  const { openLightbox } = useContext(ClassicHomeContext)
  const galleryImages = data.items
  // Horizontal scroll for gallery section
  const {
    scrollRef: galleryScrollRef,
    canScrollLeft: canScrollGalleryLeft,
    canScrollRight: canScrollGalleryRight,
    scrollLeft: scrollGalleryLeft,
    scrollRight: scrollGalleryRight,
  } = useHorizontalScroll({ deps: [data.images] })

  if (galleryImages.length === 0) {
    return null // Don't render gallery section if no images
  }
  return (
          <div className="bg-bg-secondary/25 backdrop-blur-md border border-secondary/15 px-3 py-3" {...sf('home.section.gallery')}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-['Montserrat'] font-black text-white uppercase tracking-tight">{slot.title}</h4>
              {/* #421: only when the league's Gallery section is on. */}
              {data.viewAllHref && (
                <Link to={data.viewAllHref} className="text-primary text-xs uppercase tracking-wider font-bold hover:underline" {...sf('home.gallery.view-all')}>
                  {c.viewAll}
                </Link>
              )}
            </div>
            <div className="relative">
              {canScrollGalleryLeft && (
                <button onClick={scrollGalleryLeft} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-bg-primary/60 hover:bg-bg-primary/80 border border-secondary/20 hover:border-primary/40 rounded-full flex items-center justify-center transition-all" aria-label={c.scrollGalleryLeft}>
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
              )}
              <div ref={galleryScrollRef} className="overflow-x-auto scrollbar-hide scroll-smooth" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
                  {galleryImages.map((image, index) => (
                    <button
                      key={image.id}
                      onClick={() => openLightbox(index)}
                      className="flex-shrink-0 relative w-48 sm:w-56 aspect-[16/10] bg-bg-primary/25 border border-secondary/10 overflow-hidden group cursor-pointer keep-white"
                    >
                      <img
                        src={image.thumbUrl || ''}
                        alt={image.alt}
                        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10"></div>
                      {image.title && (
                        <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-xs font-medium truncate">{image.title}</p>
                        </div>
                      )}
                      {/* Zoom icon */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-10 h-10 bg-bg-primary/40 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              {canScrollGalleryRight && (
                <button onClick={scrollGalleryRight} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-bg-primary/60 hover:bg-bg-primary/80 border border-secondary/20 hover:border-primary/40 rounded-full flex items-center justify-center transition-all" aria-label={c.scrollGalleryRight}>
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              )}
            </div>
          </div>
  )
}

// Removed. This section rendered a hardcoded "Must Watch" badge over a CSS
// play triangle on a dimmed copy of the tenant's hero image, with no href and
// no video id: there is no highlight data anywhere in the schema. Rendering
// nothing keeps the section id valid for any homepage_sections row that still
// references it, so no layout data needs migrating.
export const Highlight: HomeSectionView<'highlight'> = () => null

export const About: HomeSectionView<'about'> = ({ data }) => {
  // Hide entirely if the tenant hasn't supplied custom about copy.
  if (!data.content) return null
  return (
          <div className="bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-6 py-6" {...sf('home.section.about')}>
            <h4 className="text-sm font-['Montserrat'] font-black text-white uppercase tracking-tight mb-4">{data.title}</h4>
            <p className="text-white/75 text-sm leading-relaxed whitespace-pre-line">
              {data.content}
            </p>
          </div>
  )
}

export const Links: HomeSectionView<'links'> = ({ data }) => {
  const sections = useT(siteCoreMessages).pages.home.sections
  const quickLinks = data.items
  // Self-hide when there is nothing to link to, matching announcements and about.
  if (quickLinks.length === 0) return null
  return (
          <div className="bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-6 py-6" {...sf('home.section.links')}>
            <h4 className="text-sm font-['Montserrat'] font-black text-white uppercase tracking-tight mb-4">{sections.links}</h4>
            <div className="space-y-3">
              {quickLinks.length > 0 ? quickLinks.map((link) => (
                link.external ? (
                  // #260: an unsafe stored external_url renders as a dead button, not a link.
                  <a
                    key={link.id}
                    href={link.href ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-4 py-3 bg-bg-primary/30 border border-secondary/15 text-white hover:bg-bg-primary/40 hover:border-primary/40 transition-all text-sm uppercase tracking-wider font-bold text-center"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.id}
                    to={link.href ?? ''}
                    className="block w-full px-4 py-3 bg-bg-primary/30 border border-secondary/15 text-white hover:bg-bg-primary/40 hover:border-primary/40 transition-all text-sm uppercase tracking-wider font-bold text-center"
                  >
                    {link.label}
                  </Link>
                )
              )) : null}
            </div>
          </div>
  )
}
